import type React from 'react';

import { useGetStream } from '@vlabs/api/artemy/client';
import { StreamTargetsParameterItem } from '@vlabs/api/artemy/model';
import type { StreamId } from '@vlabs/api/artemy/model';
import { StreamStatusWithActions } from '@vlabs/monokit/entities/streams';
import { PageHeader, usePageContext } from '@vlabs/monokit/shared/ui/page';
import { withPermissions } from '@vlabs/permissions';
import { Breadcrumb, Breadcrumbs, Control } from '@vlabs/uikit';
import { generatePath, useHistory } from 'react-router-dom';

import { DeleteSourceStreamButtonWithAccess } from './DeleteSourceStreamButton';
import { useSourceStreamPageTranslation } from './i18n';
import { routes } from '../../app/routes';

const StreamStatusWithActionsWithAccess = withPermissions('stream', 'update')(StreamStatusWithActions);

interface SourceStreamPageHeaderProps {
  streamId: StreamId;
  isDirty: boolean;
  isSaving: boolean;
  onSave: VoidFunction;
}

function SourceStreamPageHeader({
  streamId,
  isDirty,
  isSaving,
  onSave,
}: SourceStreamPageHeaderProps): React.JSX.Element {
  const { t } = useSourceStreamPageTranslation();
  const history = useHistory();
  const pageCtx = usePageContext();

  // Имя потока шапка запрашивает сама (ZEN #14), как SourceLivePageHeader
  const { data: stream } = useGetStream(streamId, {
    targets: [StreamTargetsParameterItem.name],
  });
  const title = stream?.name ?? streamId;

  return (
    <PageHeader
      actions={[
        <Control.Button
          data-testid="save-stream"
          disabled={!isDirty || isSaving}
          isLoading={isSaving}
          key="save-stream"
          onClick={onSave}
          size={pageCtx.buttonSize}
        >
          {t('save')}
        </Control.Button>,
        <DeleteSourceStreamButtonWithAccess
          key="delete-stream"
          onDeleted={() => history.push(generatePath(routes.sources.list))}
          streamId={streamId}
        />,
      ]}
      breadcrumbs={(
        <Breadcrumbs>
          <Breadcrumb
            caption={t('back_to_sources')}
            href={routes.sources.list}
          />
          <Breadcrumb caption={title} />
        </Breadcrumbs>
      )}
      tag={<StreamStatusWithActionsWithAccess streamId={streamId} />}
      title={title}
    />
  );
}

export { SourceStreamPageHeader };
